import React from "react";
import PropTypes from "prop-types";
import Card from "./Card";
import Tabs from "../Tabs/Tabs";
import { useTabs } from "../../Hooks/Tabs/useTabs";

function TabsCard({ title, isLoading, initialTab, children }) {
    
    const tabChildren = React.Children.toArray(children).filter(child => child.type === Tab);
    
    const tabs = tabChildren.map((tab, idx) => {
        const label = typeof tab.props.label === "string" && tab.props.label.trim() 
            ? tab.props.label 
            : `Tab ${idx + 1}`;
        const id = tab.props.id !== undefined ? String(tab.props.id) : String(idx);
        const disabled = !!tab.props.disabled;
        return { id, label, disabled, tab };
    });
    
    // If no initialTab is provided, default to the first tab
    const effectiveInitialTab = initialTab || (tabs.length > 0 ? tabs[0] : null);
    
    const { activeTab, setActiveTab } = useTabs(effectiveInitialTab);
    
    // Sync activeTab with the fresh tabs array to get updated children
    const currentActiveTab = activeTab ? tabs.find(tab => tab.id === activeTab.id) : null;
    
    // Don't memoize - recalculate on every render to pick up changes from parent
    const getActiveTabContent = () => {
        if (!currentActiveTab?.tab) return { body: null, footer: null, bodyIsLoading: false };
        
        const children = React.Children.toArray(currentActiveTab.tab.props.children);
        const bodyChild = children.find(child => child?.type === TabBody);
        const footerChild = children.find(child => child?.type === TabFooter);
        
        return {
            body: bodyChild?.props.children || null,
            footer: footerChild?.props.children || null,
            bodyIsLoading: !!bodyChild?.props.isLoading
        };
    };
    
    const { body, footer, bodyIsLoading } = getActiveTabContent();
    
    return (
        <Card isLoading={isLoading}>
            {title && (
                <Card.CHeader>
                    <Card.Header.Title>{title}</Card.Header.Title>
                </Card.CHeader>
            )}
            <Tabs tabs={tabs} activeTab={currentActiveTab} setActiveTab={setActiveTab} />
            <Card.Content isLoading={isLoading || bodyIsLoading}>
                {body}
            </Card.Content>
            {(!isLoading && footer) && (
                <Card.Footer>{footer}</Card.Footer>
            )}
        </Card>
    );
}

function Tab({ children }) {
    return null;
}

function TabBody({ children }) {
    return null;
}

function TabFooter({ children }) {
    return null;
}

TabsCard.propTypes = {
    children: PropTypes.node.isRequired,
    isLoading: PropTypes.bool,
    title: PropTypes.node,
    initialTab: PropTypes.object
};
TabsCard.defaultProps = {
    isLoading: false,
    title: null,
    initialTab: null
};

TabsCard.Tab = Tab;
TabsCard.TabBody = TabBody;
TabsCard.TabFooter = TabFooter;

export default TabsCard;
